import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box, Paper, Button, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { configApi } from '../../../services/configApi';
import { toPercent } from '../../../utils/configUtils';
import WhitelistDrawer from './WhitelistDrawer';
import StatusChip from '../../../common/statusChip/StatusChip';

const hdSx = {
  fontFamily: 'Heebo', fontWeight: 600, color: '#362B24', fontSize: '13px',
  bgcolor: '#f9fafb', borderBottom: '1px solid #EAEAEA', py: 1.5, px: 2,
};
const cellSx = { fontFamily: 'Heebo', fontSize: '13px', color: '#362B24', px: 2, py: 1.5 };

export default function WhitelistTab({ openTrigger }) {
  const token = useSelector((state) => state?.user?.user?.token);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (openTrigger > 0) {
      setSelectedEntry(null);
      setDrawerMode('add');
      setDrawerOpen(true);
    }
  }, [openTrigger]);

  const refetch = useCallback(async () => {
    let cancelled = false;
    setLoading(true);
    try {
      const res = await configApi.listWhitelist(token);
      if (!cancelled) setEntries(res?.data ?? []);
    } catch (e) {
      if (!cancelled) toast.error(e?.error || 'Failed to load whitelist.');
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    const cancel = refetch();
    return () => { if (typeof cancel === 'function') cancel(); };
  }, [refetch]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await configApi.removeFromWhitelist(token, deleteTarget.seller_id?._id || deleteTarget._id);
      setDeleteTarget(null);
      toast.success('Seller removed from whitelist.');
      refetch();
    } catch (e) {
      if (e?.error?.toLowerCase().includes('not found')) {
        setDeleteTarget(null);
        toast.info('Entry no longer exists.');
        refetch();
      } else {
        toast.error(e?.error || 'Failed to remove seller.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const openEditDrawer = (entry) => {
    setSelectedEntry(entry);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const feeLabel = (entry) => {
    const f = entry?.app_fee;
    return f ? `Custom (${toPercent(f.percentage)})` : 'Platform default';
  };

  const shippingLabel = (entry) => {
    const s = entry?.shipping;
    if (!s?.length) return 'Platform default';
    return `Custom (${s.length} method${s.length > 1 ? 's' : ''})`;
  };

  const renderSkeletonRows = () =>
    [1, 2, 3].map((i) => (
      <TableRow key={i}>
        {[1, 2, 3, 4, 5, 6, 7].map((j) => (
          <TableCell key={j} sx={cellSx}><Skeleton /></TableCell>
        ))}
      </TableRow>
    ));

  return (
    <Box>
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
        <Table>
          <TableHead>
            <TableRow>
              {['Seller', 'Email', 'Status', 'App Fee', 'Shipping', 'Note', 'Actions'].map((h) => (
                <TableCell key={h} sx={hdSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? renderSkeletonRows() : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ ...cellSx, textAlign: 'center', py: 4, opacity: 0.5 }}>
                  No whitelisted sellers.
                </TableCell>
              </TableRow>
            ) : entries.map((entry) => (
              <TableRow key={entry._id} hover>
                <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                  {entry.seller_id?.name || 'N/A'}
                </TableCell>
                <TableCell sx={cellSx}>{entry.seller_id?.email || 'N/A'}</TableCell>
                <TableCell sx={cellSx}>
                  <StatusChip value={entry.is_active ? 'active' : 'inactive'} />
                </TableCell>
                <TableCell sx={cellSx}>{feeLabel(entry)}</TableCell>
                <TableCell sx={cellSx}>{shippingLabel(entry)}</TableCell>
                <TableCell sx={{ ...cellSx, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.note || 'N/A'}
                </TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                  <IconButton size="small" onClick={() => openEditDrawer(entry)}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteTarget(entry)}>
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <WhitelistDrawer
        open={drawerOpen}
        mode={drawerMode}
        entry={selectedEntry}
        onClose={() => setDrawerOpen(false)}
        onSuccess={() => { setDrawerOpen(false); refetch(); }}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontFamily: 'Heebo', fontWeight: 700 }}>Remove Seller</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: 'Heebo' }}>
            Remove <strong>{deleteTarget?.seller_id?.name}</strong> from the whitelist? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ fontFamily: 'Heebo', textTransform: 'none', color: '#362B24' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={handleDelete}
            sx={{ fontFamily: 'Heebo', textTransform: 'none', borderRadius: '10px' }}
          >
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
